import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { applyMeta, pageMeta, canonicalUrl } from '../seo.js';
import { findAnalysis } from '../content/index.js';
import { pageData, loadData, loadAdvertisers } from '../api.js';
import {
  advertiserLeague, talkTimeLeague, interviewDynamics, formatMonologue,
} from '../content/stats.js';
import NotFound from './NotFound.jsx';

/* ───────────────────────── the three pieces ───────────────────────── */

function Advertisers({ figures }) {
  const { scanned, brands, rows } = figures;
  const top = rows[0];
  const broadest = [...rows].sort((a, b) => b.reach - a.reach)[0];

  return (
    <>
      <p>
        Podcast advertising is famously hard to see from the outside. Networks do not publish
        their sponsor lists, and the ads are read aloud rather than served as tracked
        impressions, so there is nothing for a crawler to count. But we already transcribe
        every episode in order to measure talk time, and stripping the sponsor reads out is
        part of that. Rather than throw those segments away, we count them.
      </p>
      <p>
        Across {scanned?.toLocaleString()} episodes we identified {brands} distinct
        advertisers. Here are the ones that turn up most.
      </p>

      <div className="table-scroll">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Advertiser</th>
              <th scope="col" style={{ textAlign: 'right' }}>Ad reads</th>
              <th scope="col" style={{ textAlign: 'right' }}>Shows</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.key}>
                <td>{i + 1}</td>
                <td>{row.name}</td>
                <td style={{ textAlign: 'right' }}>{row.total.toLocaleString()}</td>
                <td style={{ textAlign: 'right' }}>{row.reach}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Volume is not the same as reach</h2>
      <p>
        The two right-hand columns tell different stories, and the gap between them is the
        interesting part. {top?.name} leads on raw volume with{' '}
        {top?.total.toLocaleString()} reads, but a large share of those come from a single
        heavily sponsored show. {broadest?.name}, by contrast, appears across{' '}
        {broadest?.reach} different podcasts.
      </p>
      <p>
        That is the difference between buying a show and buying an audience. A brand
        concentrated on one podcast is betting on that host's endorsement; a brand spread
        across a dozen is buying the medium itself and does not much mind whose voice reads
        the copy. Both strategies are visible in the table, and neither is obvious from
        listening to any single episode.
      </p>

      <h2>How the counting works</h2>
      <p>
        Sponsor blocks are found by what is said rather than where it sits, which is what
        lets the same method catch pre-roll, mid-roll, post-roll and dynamically inserted
        ads. Brand names are matched inside those blocks. One read counts once, however many
        times the brand is repeated within it.
      </p>
      <p>
        The undercount is real: a novel sponsor read in an unfamiliar format can slip past
        the detector, and programmatically inserted ads vary between listeners, so what we
        transcribed is what one download contained rather than what everyone heard. Treat
        these as a solid floor rather than a complete census.{' '}
        <Link to="/methodology">The methodology page</Link> covers the detection in more
        detail.
      </p>
    </>
  );
}

function TalkTime({ figures }) {
  const rows = figures;
  const most = rows[0];
  const least = rows[rows.length - 1];

  return (
    <>
      <p>
        Every host has a reputation: this one lets guests speak, that one talks over
        everybody. Almost none of it is based on measurement. Below is the actual answer for
        every show we track, ranked by the median share of conversation the host holds.
      </p>
      <p>
        Three exclusions, all of which change the ranking. Solo episodes are out: a host
        talking 98% of a monologue tells you nothing about how they behave with someone else
        in the room, and leaving them in flatters whichever show runs the most of them.
        Panel shows are out too, because every regular counts as a host and their combined
        share approaches 100% whether or not a guest turned up, which would park them at the
        top of the table for a structural reason rather than a behavioural one. And a show
        needs at least five measured episodes to appear at all.
      </p>

      <div className="table-scroll">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Podcast</th>
              <th scope="col" style={{ textAlign: 'right' }}>Median host share</th>
              <th scope="col" style={{ textAlign: 'right' }}>Range</th>
              <th scope="col" style={{ textAlign: 'right' }}>Episodes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.uuid}>
                <td>{i + 1}</td>
                <td>
                  <Link to={`/podcast/${row.uuid}`}>{row.name}</Link>
                  {row.hostName && <span className="muted-inline"> · {row.hostName}</span>}
                </td>
                <td style={{ textAlign: 'right' }}>{row.medianHostPct}%</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {row.minHostPct}–{row.maxHostPct}%
                </td>
                <td style={{ textAlign: 'right' }}>{row.episodes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>The range matters more than the median</h2>
      <p>
        A host sitting near 50% is splitting the conversation evenly, which is what most
        interview formats aim for. The number worth looking at, though, is the spread in the
        range column. A narrow range means a host who behaves the same way whoever is sitting
        opposite them: a format, essentially. A wide one means a host who genuinely adapts,
        receding for a guest with something to say and carrying the episode when the guest
        does not.
      </p>
      <h2>The top of the table is a format, not a personality</h2>
      <p>
        The shows clustered at the top have something in common that has nothing to do with
        their hosts being talkative. They are narrated: the host is a narrator carrying a
        produced story, and the other voices are interview tape cut into it. {most?.name}{' '}
        leads at {most?.medianHostPct}% for that reason, not because anyone is talking over
        a guest.
      </p>
      <p>
        The interview shows sit lower down and cluster much more tightly, mostly somewhere
        around an even split, which is what the format is trying to achieve.{' '}
        {least?.name} anchors the bottom at {least?.medianHostPct}%, a host who books people
        with something to say and then largely gets out of the way.
      </p>
      <p>
        None of this is a criticism, and the ranking is not a quality order. It tells you
        what kind of hour you are about to spend, which is the entire point of the site.
      </p>
      <p>
        Every figure here is a share of ad-free, pre-roll-free conversation. See{' '}
        <Link to="/methodology">the methodology</Link> for what that excludes and why.
      </p>
    </>
  );
}

function Interviews({ figures }) {
  const rows = figures;
  const asker = rows[0];
  const talker = rows[rows.length - 1];
  const record = [...rows]
    .filter(r => r.longestMonologueMs != null)
    .sort((a, b) => b.longestMonologueMs - a.longestMonologueMs)[0];

  return (
    <>
      <p>
        Talk share tells you how much of an episode a host occupied. It does not tell you
        what they were doing with it. A host at 45% who spends that time asking questions is
        running a very different show from one at 45% who is trading stories.
      </p>
      <p>
        Three measurements separate them: the share of all questions that came from the host,
        how often the conversation changed hands, and the longest anyone spoke without being
        interrupted.
      </p>

      <div className="table-scroll">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th scope="col">Podcast</th>
              <th scope="col" style={{ textAlign: 'right' }}>Host's share of questions</th>
              <th scope="col" style={{ textAlign: 'right' }}>Median turn changes</th>
              <th scope="col" style={{ textAlign: 'right' }}>Longest monologue</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.uuid}>
                <td>
                  <Link to={`/podcast/${row.uuid}`}>{row.name}</Link>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {Math.round(row.medianQuestionRatio * 100)}%
                </td>
                <td style={{ textAlign: 'right' }}>
                  {row.medianTurnSwitches?.toLocaleString() ?? '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {formatMonologue(row.longestMonologueMs) || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Interviewers</h2>
      <p>
        At the top of the table sits {asker?.name}, where{' '}
        {Math.round((asker?.medianQuestionRatio ?? 0) * 100)}% of the questions in a typical
        episode come from the host. That is the signature of a proper interview: the host is
        there to extract something, and the guest is there to answer.
      </p>

      <h2>Conversations</h2>
      <p>
        Lower down, the ratio evens out. When both sides are asking questions at similar
        rates, as on {talker?.name}, the format has stopped being an interview and become a
        conversation. Neither party is running it. These episodes also tend to show far more
        turn changes, because nobody is waiting for their turn to finish a prepared answer.
      </p>

      <h2>And then there are monologues</h2>
      <p>
        The right-hand column is the longest single stretch we have measured of one person
        speaking without the other saying anything. The record is{' '}
        {formatMonologue(record?.longestMonologueMs)}, on {record?.name}. Neither a pre-roll
        nor an ad read, both of which are stripped before anything is measured.
      </p>
      <p>
        Read that column together with the show's format rather than on its own. On a
        narrated programme a long monologue is simply the narration doing its job, and the
        figure says nothing about anyone being talked over. It is on the conversational
        shows, where a long uninterrupted run is not the format, that the number is actually
        telling you something.
      </p>
      <p>
        A caveat we would rather state than bury. Question counts come from counting question
        marks in the transcript, and storytellers produce plenty of those without asking
        anything: "and I was like, what?" is punctuated as a question. On comedy and
        storytelling shows the rhetorical rate runs between 60% and 80%, so these are
        reviewed by hand and the rhetorical ones removed before publication. It is the
        softest measurement on the site, and{' '}
        <Link to="/methodology">the methodology page</Link> says so at more length.
      </p>
    </>
  );
}

/* ───────────────────────── shell ───────────────────────── */

const RENDERERS = {
  'who-advertises-on-podcasts': Advertisers,
  'talk-time-league-table': TalkTime,
  'interviewers-and-monologuists': Interviews,
};

/**
 * Compute an article's figures from the raw datasets. Shared by the prerenderer
 * (at build time) and the browser (after a client-side navigation), so both
 * render exactly the same numbers.
 */
export function analysisFigures(slug, { data, advertisers }) {
  switch (slug) {
    case 'who-advertises-on-podcasts': return advertiserLeague(advertisers);
    case 'talk-time-league-table':     return talkTimeLeague(data);
    case 'interviewers-and-monologuists': return interviewDynamics(data);
    default: return null;
  }
}

export default function Analysis() {
  const { slug } = useParams();
  const article = findAnalysis(slug);
  const boot = pageData(`analysis:${slug}`);
  const [figures, setFigures] = useState(boot?.figures ?? null);

  useEffect(() => {
    if (!article) return;
    return applyMeta(pageMeta({
      path: `/analysis/${slug}`,
      title: article.title,
      description: article.description,
      type: 'article',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        datePublished: article.published,
        url: canonicalUrl(`/analysis/${slug}`),
        author: { '@type': 'Organization', name: 'TalkMuch' },
        publisher: { '@type': 'Organization', name: 'TalkMuch' },
      },
    }));
  }, [article, slug]);

  useEffect(() => {
    if (!article || figures) return;
    let cancelled = false;
    const needsAdvertisers = slug === 'who-advertises-on-podcasts';
    Promise.all([
      needsAdvertisers ? Promise.resolve(null) : loadData(),
      needsAdvertisers ? loadAdvertisers() : Promise.resolve(null),
    ])
      .then(([data, advertisers]) => {
        if (cancelled) return;
        setFigures(analysisFigures(slug, { data, advertisers }));
      })
      .catch(err => console.error('Failed to load analysis data:', err));
    return () => { cancelled = true; };
  }, [article, slug, figures]);

  if (!article) return <NotFound />;

  const Body = RENDERERS[slug];

  return (
    <article className="page-prose">
      <Link to="/podcasts" className="back-link" aria-label="Back to podcasts">
        &larr; All podcasts
      </Link>
      <h1 className="page-heading">{article.title}</h1>
      <p className="page-subheading">{article.blurb}</p>

      {figures ? <Body figures={figures} /> : (
        <p className="muted-inline">Loading the figures…</p>
      )}
    </article>
  );
}
